/* eslint-disable jsdoc/require-jsdoc, no-nested-ternary */
import { ReactElement, useEffect, useState } from 'react';
import axiosInstance from '../_helpers/interceptor';
import { DEV_DOMAIN_URL } from '../environment';
import { downloadInvoice, InvoicePayment } from '../_helpers/invoice';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';

type PaymentHistoryItem = {
  id: number;
  subscriptionId: number;
  paypalPaymentId: string;
  paypalPayerId: string;
  amount: number;
  currency: string;
  status: string;
  paymentDate: string | null;
  planName: string;
  subscriptionStatus: string;
  subscriptionStartDate: string | null;
  subscriptionEndDate: string | null;
  /** Devices this payment bought the plan for. */
  serialNumbers: number[];
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusBadge(status: string): string {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS' || s === 'APPROVED')
    return 'bg-success-500/15 text-success-400 border-success-500/30';
  if (s === 'PENDING') return 'bg-warning-500/15 text-warning-300 border-warning-500/30';
  if (s === 'FAILED') return 'bg-error-500/15 text-error-400 border-error-500/30';
  if (s === 'EXPIRED') return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
  return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
}

export default function PaymentHistoryPage(): ReactElement {
  const [items, setItems] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await axiosInstance.get(`${DEV_DOMAIN_URL}/paypal/my-payments`);
        if (cancelled) return;
        setItems(res.data || []);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || 'Failed to load payment history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownload = (item: PaymentHistoryItem): void => {
    const payment: InvoicePayment = {
      id: item.id,
      amount: item.amount,
      currency: item.currency,
      paymentDate: item.paymentDate,
      paypalPaymentId: item.paypalPaymentId,
      paypalPayerId: item.paypalPayerId,
      status: item.status,
      planName: item.planName,
      subscriptionId: item.subscriptionId,
      serialNumbers: item.serialNumbers || []
    };
    const clientName = sessionStorage.getItem('client_name') || '';
    downloadInvoice(payment, {
      name: clientName,
      email: getUserNameFromSessionStorage()
    });
  };

  const totalPaid = items
    .filter((p) => (p.status || '').toLowerCase() === 'approved')
    .reduce((acc, p) => acc + (p.amount || 0), 0);

  return (
    <div className="flex flex-1 flex-col gap-6 px-6 py-6 text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Payment History</h1>
          <p className="text-sm text-gray-400">Device plan payments and invoices</p>
        </div>
        <div className="flex gap-3">
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Payments</div>
            <div className="text-xl font-semibold">{items.length}</div>
          </div>
          <div className="rounded border border-gray-800 bg-container-500 px-4 py-3 text-sm">
            <div className="text-gray-400">Total Paid</div>
            <div className="text-xl font-semibold">${totalPaid.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-gray-400">
          <span className="material-icons-outlined animate-spin">autorenew</span>
          Loading…
        </div>
      ) : error ? (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {error}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-container-500 px-6 py-12 text-center">
          <div className="text-gray-400">You don&apos;t have any payments yet.</div>
          <a
            href="/pricing"
            className="mt-4 inline-block text-primary-400 hover:text-primary-300"
          >
            View plans →
          </a>
        </div>
      ) : (
        <section className="rounded-lg border border-gray-800 bg-container-500">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background-500 text-left text-gray-400">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Devices</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">PayPal Payment</th>
                  <th className="px-4 py-3 text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const isPaid = (item.status || '').toLowerCase() === 'approved';
                  return (
                    <tr key={item.id} className="border-t border-gray-800">
                      <td className="px-4 py-3">{formatDate(item.paymentDate)}</td>
                      <td className="px-4 py-3">{item.planName}</td>
                      <td className="px-4 py-3" title={(item.serialNumbers || []).map((n) => `#${n}`).join(', ')}>
                        {item.serialNumbers?.length
                          ? item.serialNumbers.length <= 3
                            ? item.serialNumbers.map((n) => `#${n}`).join(', ')
                            : `${item.serialNumbers.length} devices`
                          : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {item.currency} {item.amount?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block rounded border px-2 py-0.5 text-xs ${statusBadge(
                            item.status
                          )}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">
                        {item.paypalPaymentId}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleDownload(item)}
                          disabled={!isPaid}
                          className={`inline-flex items-center gap-1 rounded border px-3 py-1 text-xs ${
                            isPaid
                              ? 'border-primary-500/40 bg-primary-500/10 text-primary-300 hover:bg-primary-500/20'
                              : 'border-gray-700 bg-gray-800/40 text-gray-500 cursor-not-allowed'
                          }`}
                          title={isPaid ? 'Download Invoice' : 'Invoice only available for paid payments'}
                        >
                          <span className="material-icons-outlined text-sm">download</span>
                          Invoice
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
